import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, SlidersHorizontal, ShoppingBag, ArrowUpRight, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { formatRupiah } from "@/lib/dummyData";
import { ProductAPI } from "@/lib/api";
import { useCart } from "@/lib/cart";

// Petakan produk backend ke bentuk yang dipakai kartu produk.
function mapProduct(p) {
  return {
    id: p.id,
    name: p.name,
    category: p.category,
    image: p.image,
    price: Number(p.price_non_member),
    memberPrice: Number(p.price_member),
    priceForYou: Number(p.price_for_you),
    stock: p.stock,
    tags: p.source_type === "VENDOR_ANGGOTA" ? ["Produk Anggota"] : [],
  };
}

export default function Shop() {
  const [cat, setCat] = useState("Semua");
  const [query, setQuery] = useState("");
  const [price, setPrice] = useState([0, 0]);
  const [maxPrice, setMaxPrice] = useState(0);
  const [sort, setSort] = useState("terbaru");
  // Katalog selalu dari backend (tanpa data contoh) agar yang tampil = produk asli.
  const [products, setProducts] = useState([]);
  const [productCategories, setProductCategories] = useState(["Semua"]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const { addItem } = useCart();

  // Tambah produk ke keranjang. Tamu → keranjang lokal, anggota → keranjang backend.
  const addToCart = async (p) => {
    try {
      await addItem(p, 1);
      toast.success(`${p.name} ditambahkan ke keranjang`);
    } catch (err) {
      if (err?.message === "DUMMY_PRODUCT") return toast.info("Produk contoh — hubungkan backend untuk belanja");
      toast.error("Gagal menambah ke keranjang");
    }
  };

  useEffect(() => {
    let alive = true;
    ProductAPI.list().then((rows) => {
      if (!alive) return;
      const mapped = (rows || []).map(mapProduct);
      const top = Math.max(0, ...mapped.map((p) => p.price));
      const ceil = Math.ceil(top / 10000) * 10000 || 100000;
      setProducts(mapped);
      setMaxPrice(ceil);
      setPrice([0, ceil]);
    }).catch(() => { if (alive) setLoadError(true); })
      .finally(() => { if (alive) setLoading(false); });
    ProductAPI.categories().then((cats) => {
      if (alive && cats?.length) setProductCategories(cats);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    let arr = products.filter((p) =>
      (cat === "Semua" || p.category === cat) &&
      p.name.toLowerCase().includes(query.toLowerCase()) &&
      p.price >= price[0] && p.price <= price[1]
    );
    if (sort === "harga-asc") arr = [...arr].sort((a, b) => a.price - b.price);
    if (sort === "harga-desc") arr = [...arr].sort((a, b) => b.price - a.price);
    return arr;
  }, [products, cat, query, price, sort]);

  return (
    <div className="mx-auto max-w-7xl px-5 lg:px-10 py-10 lg:py-14">
      <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-5 mb-10">
        <div>
          <div className="pill-tag bg-amber-warm/15 text-amber-700 mb-3">Toko Koperasi · Harga Publik</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold text-ink tracking-tight">
            Belanja dari <span className="font-editorial italic">tangan anggota</span>
          </h1>
          <p className="text-ink/75 mt-3 max-w-xl">Produk lokal pilihan dari petani, UMKM, dan kelompok tani binaan koperasi.</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/masuk" className="text-sm text-forest font-semibold inline-flex items-center gap-1">
            Login untuk harga anggota <ArrowUpRight size={14} />
          </Link>
        </div>
      </header>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Sidebar filter */}
        <aside className="lg:col-span-3 space-y-6 lg:sticky lg:top-24 self-start">
          <div className="bg-white rounded-2xl border border-stone-soft p-5">
            <div className="flex items-center gap-2 text-ink mb-4">
              <SlidersHorizontal size={16} /> <span className="font-display font-semibold">Filter</span>
            </div>
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/55" />
              <Input
                placeholder="Cari produk..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9 bg-cream border-stone-soft rounded-lg"
                data-testid="shop-search-input"
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-stone-soft p-5">
            <div className="text-xs uppercase tracking-wider text-ink/55 font-semibold mb-3">Kategori</div>
            <div className="space-y-1.5">
              {productCategories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  data-testid={`shop-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${
                    cat === c ? "bg-forest text-white" : "text-ink/75 hover:bg-cream"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-stone-soft p-5">
            <div className="text-xs uppercase tracking-wider text-ink/55 font-semibold mb-4">Rentang Harga</div>
            <Slider value={price} onValueChange={setPrice} min={0} max={maxPrice || 100000} step={1000} className="my-3" />
            <div className="flex justify-between text-xs text-ink/75">
              <span>{formatRupiah(price[0])}</span>
              <span>{formatRupiah(price[1])}</span>
            </div>
          </div>
        </aside>

        {/* Product grid */}
        <section className="lg:col-span-9">
          <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
            <div className="text-sm text-ink/75">
              Menampilkan <span className="font-semibold text-ink">{filtered.length}</span> produk
              {cat !== "Semua" && (
                <Badge className="ml-2 bg-forest/10 text-forest hover:bg-forest/10 border-0 rounded-full">
                  {cat} <button onClick={() => setCat("Semua")} className="ml-1"><X size={11} /></button>
                </Badge>
              )}
            </div>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="shop-sort"
              className="text-sm bg-white border border-stone-soft rounded-full px-4 py-2 focus:ring-2 focus:ring-sage focus:outline-none"
            >
              <option value="terbaru">Terbaru</option>
              <option value="harga-asc">Harga Terendah</option>
              <option value="harga-desc">Harga Tertinggi</option>
            </select>
          </div>

          {loading ? (
            <div className="rounded-3xl border border-stone-soft p-16 text-center bg-white text-sm text-ink/55">Memuat produk…</div>
          ) : filtered.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-stone-soft p-16 text-center bg-white">
              <div className="text-3xl">🌾</div>
              <h3 className="font-display font-semibold text-ink mt-3">
                {loadError ? "Katalog sedang tidak dapat dimuat" : products.length === 0 ? "Produk segera hadir" : "Belum ada produk yang cocok"}
              </h3>
              <p className="text-sm text-ink/75 mt-1">
                {loadError ? "Silakan muat ulang halaman beberapa saat lagi." : products.length === 0 ? "Katalog sedang disiapkan." : "Coba ubah filter atau cari dengan kata lain."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
              {filtered.map((p) => (
                <article key={p.id} className="group rounded-2xl bg-white border border-stone-soft overflow-hidden hover-lift" data-testid={`product-card-${p.id}`}>
                  <Link to={`/toko/${p.id}`} className="block relative aspect-square overflow-hidden bg-cream">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    {p.tags && p.tags.length > 0 && (
                      <Badge className="absolute top-3 left-3 bg-terracotta hover:bg-terracotta text-white border-0 rounded-full text-[10px] uppercase tracking-wider">
                        {p.tags[0]}
                      </Badge>
                    )}
                  </Link>
                  <div className="p-4">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-ink/55">{p.category}</div>
                    <Link to={`/toko/${p.id}`}>
                      <h3 className="font-medium text-ink text-sm mt-1 line-clamp-2 min-h-[2.4rem] hover:text-forest transition">{p.name}</h3>
                    </Link>
                    <div className="text-xs mt-2 text-ink/55">{p.stock > 0 ? `Stok ${p.stock}` : "Stok habis"}</div>
                    <div className="flex items-center justify-between mt-3">
                      <div>
                        <div className="font-display font-bold text-ink">{formatRupiah(p.price)}</div>
                        <div className="text-[11px] text-amber-700">/anggota {formatRupiah(p.memberPrice)}</div>
                      </div>
                      <Button
                        size="icon"
                        className="rounded-full bg-forest hover:bg-forest-dark text-white w-10 h-10"
                        onClick={() => addToCart(p)}
                        data-testid={`product-add-${p.id}`}
                      >
                        <ShoppingBag size={16} />
                      </Button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
